import { z } from 'zod'
import { MODE } from '../../../data/queryFactories'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { majors } from '../../../data/account_constants'
import { toTitleCase } from '../../../utils/conversion'

import FormPageHeader from '../FormPageHeader'
import FormField from '../../../components/function/form/FormField'
import DeleteConfirm from '../account-list/DeleteConfirm'
import { PATH } from '../../../data/router_path'
import useGetAccountGroup from '../account-list/hooks/useGetAccountGroup'
import { useNav } from '../../../hooks/useNav'
const guestAccountSchema = z.object({
  id: z.string().regex(/^\d{8}$/, { message: 'Mã tài khoản bao gồm 8 chữ số' }),
  name: z
    .string()
    .regex(/^[\p{L}\s]+$/u, { message: 'Tên không thể có kí tự đặc biệt' }),
  'expire-date': z.string().optional(),
  groups: z
    .array(z.string())
    .min(1, { message: 'Tài khoản phải thuộc ít nhất một nhóm' }),
  personalEmail: z
    .string()
    .email({ message: 'Vui lòng nhập đúng định dạng email' })
    .trim(),
  phoneNumber: z
    .string()
    .regex(/^\d+$/, { message: 'Số điện thoại chỉ được bao gồm số' }),
})

export default function AccountGuestForm({
  mode,
  initForm,
  submitHandler,
  uuid,
}) {
  const {
    watch,
    register,
    control,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(guestAccountSchema),
    defaultValues: initForm,
  })
  const navigate = useNav()
  const [className, id, fullName] = watch(['className', 'id', 'fullName'])
  const onSubmit = (formData) => {
    const dataTransformed = {
      schoolId: formData.id,
      fullName: formData.name,
      schoolEmail: `${formData.id}@st.neu.edu.vn`,
      username: formData.id,
      password: formData.id,
      personalEmail: formData.personalEmail || '',
      phoneNumber: formData.phoneNumber || '',
      studentCourse: formData.studentCourse || '',
      major: formData.className || '',
      groups: formData.groups || [],
    }
    submitHandler(dataTransformed)
  }
  useEffect(() => {
    if (className) {
      const mapping = majors[className]
      if (mapping) {
        setValue('faculty', mapping.faculty)
        // clearErrors('faculty')
        setValue('school', mapping.school)
        clearErrors(['school', 'faculty'])
      }
    }
    // if (id) {
    //   setValue('schoolEmail', `${id}@st.neu.edu.vn`)
    //   if (id.length === 8) {
    //     clearErrors('schoolEmail')
    //   }
    // }
    if (fullName) {
      // consolel
      setValue('fullName', toTitleCase(fullName))
    }
  }, [className, fullName, setValue, clearErrors])
  const getAccountInfo = () => {
    switch (mode) {
      case MODE.CREATE:
        return {
          title: `Tạo tài khoản khách`,
          url: PATH.ACCOUNT.GUEST.CREATE,
          actionName: 'Tạo',
        }
      case MODE.EDIT:
        return {
          title: `Chỉnh sửa tài khoản khách ${initForm.id}`,
          url: PATH.ACCOUNT.GUEST.EDIT(uuid),
          actionName: 'Cập nhật',
        }
      default:
        return { title: '', url: '', actionName: '' }
    }
  }
  const { title, url, actionName } = getAccountInfo()
  const { data: groupData } = useGetAccountGroup()
  const textFieldData = [
    {
      label: 'Mã tài khoản',
      type: 'text',
      name: 'id',
      length: 8,
      required: true,
      disabled: false,
    },
    { label: 'Tên tài khoản', type: 'text', name: 'name', required: true },
    {
      label: 'Nhóm người dùng',
      type: 'checkbox',
      name: 'groups',
      required: true,
      options: (groupData?.groups ?? [])?.map((item) => ({
        value: item?.uuid,
        label: item?.name,
      })),
    },
    {
      label: 'Khoá',
      type: 'text',
      name: 'studentCourse',
      search: false,
      required: false,
    },
    {
      label: 'Email trường',
      type: 'email',
      name: 'schoolEmail',
      required: false,
    },
    {
      label: 'Ngành học',
      type: 'text',
      name: 'major',
      required: false,
      search: true,
      placeholder: 'Vui lòng điền ngành học',
    },
    {
      label: 'Email cá nhân',
      type: 'email',
      name: 'personalEmail',
      required: true,
    },

    {
      label: 'Số điện thoại',
      type: 'text',
      name: 'phoneNumber',
      length: 10,
      required: true,
    },
  ]
  const handleClosePopUp = () => {
    const confirm = window.confirm(
      'Bạn có chắc chắn muốn rời khỏi trang tạo tài khoản khách!'
    )
    if (confirm) navigate(PATH.ACCOUNT.GUEST.LIST)
  }
  return (
    <section className="h-screen space-y-2">
      <form
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
        className="space-y-4"
      >
        <FormPageHeader
          title={title}
          actionName={actionName}
          disableSubmit={Object.keys(errors).length > 0}
          url={url}
          closePopUp={handleClosePopUp}
        />
        <div className="grid grid-cols-2 gap-4">
          {textFieldData.map((field) => (
            <div
              key={field.name}
              className={
                field.name === 'expire-date' ? 'col-span-2' : 'col-span-1'
              }
            >
              {' '}
              <FormField
                key={field.name}
                {...field}
                register={register}
                errors={errors}
                control={control}
                placeholder={field?.placeholder}
                options={field?.options}
                size={field?.length}
              />
            </div>
          ))}
        </div>
      </form>
      {mode === MODE.EDIT && <DeleteConfirm deleteUUID={initForm.uuid} />}
    </section>
  )
}
